import React, { useContext, useState } from "react";
import { mdiContentSave, mdiPlusBox } from "@mdi/js";
import { useLocation } from "wouter";
import intl from "react-intl-universal";
import styled from "styled-components";

import entryRest from "../../../../rest/entryRest";
import antennaRest from "../../../../rest/antennaRest";
import commonUtils from "../../../../script/utils/commonUtils";

import { Text } from "../../../components/Text";
import { Spacer } from "../../../components/Spacer";
import { Button } from "../../../components/Button";
import { useOnChange } from "../../../hooks/useOnChange";
import { ContentBody } from "../../../containers/ContentBody";
import { ContentHeader } from "../../../containers/ContentHeader";

import { SectorForm } from "./SectorForm";
import { useInput } from "../../../hooks/useInput";
import { Tooltip } from "../../../components/Tooltip";
import { AntennaSystemForm } from "./AntennaSystemForm";
import { InputText } from "../../../components/InputText";
import { useValidation } from "../../../hooks/useValidation";
import { SelectSingle } from "../../../components/SelectSingle";
import { ValidationContext } from "../../../contexts/ValidationContext";

const ColumnStyle = styled.div`
    padding-right: 8px;
    width: 130px;
    margin: 1px;
    >div {
        height: 32px;
    }
`;

const AntennaNameRowStyle = styled.div`
    display: flex;
    cursor: pointer;
    &:hover {
        background: ${props => props.theme.greyLightest};
    }
    >div:nth-child(1){
        min-width: 230px;
        overflow: hidden;
    }
`;

const ContentStyle = styled.div`
    display: flex;
`;

export const MultiRadiosEntry = () => {

    const validationContext = useContext(ValidationContext);
    const [selectedItems, setSelectedItems] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [antennaSystems, setAntennaSystems] = useState([]);
    const [entry, setEntry] = useState({
        id: null,
        name: null,
        calcSetting: {},
        sectors: [],
        plans: [],
    });
    const [vendorOptions, setVendorOptions] = useState({});

    const operator = useInput("");
    const vendor = useInput("");
    const antennaManufacturer = useInput("");
    const antennaName = useInput("antennaName-" + 0, "");
    const siteNum = useInput("siteNum", "");
    const entryName = useInput("name", "");
    const [saving, setSaving] = useState(false);
    const [, setLocation] = useLocation();

    useValidation(value => !commonUtils.isBlank(value), [siteNum]);
    useValidation(value => !commonUtils.isBlank(value), [entryName]);

    const clearDependentData = () => {
        setAntennaSystems([]);
        setSectors([]);
    };

    // vendor list mapping
    const vendorMapping = {
        TELEKOM: {1: "Ericsson", 2: "Huawei"},
        VODAFONE: {1: "Ericsson", 2: "Huawei", 3: "Vodafone"},
        TELEFONICA: {1: "Nokia", 2: "Huawei"},
        _1UND1: {1: "1&1"},
        TOWERS_AT: {1: "Towers AT"},
    };

    const initData = async antennaNameValue => {
        clearDependentData();

        if (commonUtils.isBlank(antennaNameValue) || commonUtils.isBlank(operator.value) || commonUtils.isBlank(vendor.value)) {
            return;
        }

        const operatorValue = operator.value;
        const vendorValue = vendor.value;
        const antennaSystemItems = await antennaRest.getAntennaSystems(antennaNameValue);
        if (!antennaSystemItems || !Array.isArray(antennaSystemItems) || antennaSystemItems.length === 0) {
            return;
        }

        // Define the allowed antenna systems for each operator
        const antennaSystemRules = {
            TELEKOM: [],
            VODAFONE: ["MB07", "MB08", "MB09", "MB15", "MB18", "MB21", "MB26", "MB35"],
            TELEFONICA: ["MB07", "MB08", "MB09", "MB18", "MB21", "MB26", "MB35"],
            _1UND1: ["MB21", "MB26", "MB35"],
            TOWERS_AT: ["MB07", "MB08", "MB09", "MB15", "MB18", "MB21", "MB26", "MB35"],
        };

        const frequencyRules = {
            TELEKOM: { MB07: 768, MB08: 811, MB09: 945, MB15: 1452, MB18: 1805, MB21: 2150, MB26: 2640, MB35: 3610 },
            TELEFONICA: { MB07: 758, MB08: 791, MB09: 925, MB18: 1835, MB21: 2130, MB26: 2660, MB35: 3540 },
            VODAFONE: { MB07: 778, MB08: 801, MB09: 935, MB15: 1472, MB18: 1855, MB21: 2110, MB26: 2620, MB35: 3400 },
            _1UND1: { MB21: 2140, MB26: 2660, MB35: 3490 },
            TOWERS_AT: { MB07: 768, MB08: 811, MB09: 925, MB15: 1450, MB18: 1805, MB21: 2135, MB26: 2640, MB35: 3690 }
        };

        const numChannelsRules = {
            TELEKOM: { MB07: 1, MB08: 1, MB09: 1, MB15: 1, MB18: 1, MB21: 1, MB26: 1, MB35: 1 },
            TELEFONICA: { MB07: 1, MB08: 1, MB09: 1, MB15: 1, MB18: 1, MB21: 1, MB26: 1, MB35: 1 },
            VODAFONE: { MB07: 1, MB08: 1, MB09: 1, MB15: 1, MB18: 1, MB21: 1, MB26: 1, MB35: 1 },
            _1UND1: { MB07: 1, MB08: 1, MB09: 1, MB15: 1, MB18: 1, MB21: 1, MB26: 1, MB35: 1 },
            TOWERS_AT: { MB07: 2, MB08: 2, MB09: 4, MB15: 4, MB18: 4, MB21: 4, MB26: 4, MB35: 1 }
        };

        const powerRules = {
            TELEKOM: {
                VENDOR_1: { MB07: [61], MB08: [61], MB09: [96], MB15: [122], MB18: [90], MB21: [60], MB26: [30], MB35: [150] },
                VENDOR_2: { MB07: [61], MB08: [61], MB09: [96], MB15: [122], MB18: [90], MB21: [60], MB26: [30], MB35: [150] }
            },
            TELEFONICA: {
                VENDOR_1: { MB07: [80], MB08: [80], MB09: [80], MB15: [], MB18: [160], MB21: [160], MB26: [240], MB35: [240] },
                VENDOR_2: { MB07: [80], MB08: [80], MB09: [80], MB15: [], MB18: [160], MB21: [160], MB26: [240], MB35: [240] }
            },
            VODAFONE: {
                VENDOR_1: { MB07: [46], MB08: [46], MB09: [184], MB15: [92], MB18: [92], MB21: [92], MB26: [92], MB35: [228] },
                VENDOR_2: { MB07: [52], MB08: [52], MB09: [208], MB15: [104], MB18: [104], MB21: [104], MB26: [104], MB35: [208] },
                VENDOR_3: { MB07: [71], MB08: [71], MB09: [214], MB15: [284], MB18: [214], MB21: [284], MB26: [142], MB35: [284] }
            },
            _1UND1: {
                VENDOR_1: { MB07: [], MB08: [], MB09: [], MB15: [], MB18: [], MB21: [240], MB26: [160], MB35: [50] },
                VENDOR_2: { MB07: [], MB08: [], MB09: [], MB15: [], MB18: [], MB21: [240], MB26: [160], MB35: [50] }
            },
            TOWERS_AT: {
                VENDOR_1: { MB07: [30], MB08: [20], MB09: [27.5], MB15: [30], MB18: [40], MB21: [30], MB26: [40], MB35: [137.2] },
                VENDOR_2: { MB07: [30], MB08: [20], MB09: [27.5], MB15: [30], MB18: [40], MB21: [30], MB26: [40], MB35: [137.2] }
            }
        };

        const attenuationRules = {
            TELEKOM: {
                VENDOR_1: { MB07: [0.2], MB08: [0.2], MB09: [0.2], MB15: [0.2], MB18: [0.2], MB21: [0.2], MB26: [0.2], MB35: [0] },
                VENDOR_2: { MB07: [1], MB08: [1], MB09: [1], MB15: [1], MB18: [1], MB21: [1], MB26: [1], MB35: [0] }
            },
            TELEFONICA: {
                VENDOR_1: { MB07: [0], MB08: [0], MB09: [0], MB15: [], MB18: [0], MB21: [0], MB26: [0], MB35: [0] },
                VENDOR_2: { MB07: [0.5], MB08: [0.5], MB09: [0.5], MB15: [], MB18: [0.5], MB21: [0.5], MB26: [0.5], MB35: [0] }
            },
            VODAFONE: {
                VENDOR_1: { MB07: [0], MB08: [0], MB09: [0], MB15: [0], MB18: [0], MB21: [0], MB26: [0], MB35: [0] },
                VENDOR_2: { MB07: [0], MB08: [0], MB09: [0], MB15: [0], MB18: [0], MB21: [0], MB26: [0], MB35: [0] },
                VENDOR_3: { MB07: [0], MB08: [0], MB09: [0], MB15: [0], MB18: [0], MB21: [0], MB26: [0], MB35: [0] }
            },
            _1UND1: {
                VENDOR_1: { MB07: [], MB08: [], MB09: [], MB15: [], MB18: [], MB21: [0], MB26: [0], MB35: [0] },
                VENDOR_2: { MB07: [], MB08: [], MB09: [], MB15: [], MB18: [], MB21: [0], MB26: [0], MB35: [0] }
            },
            TOWERS_AT: {
                VENDOR_1: { MB07: [0], MB08: [0], MB09: [0], MB15: [0], MB18: [0], MB21: [0], MB26: [0], MB35: [0] },
                VENDOR_2: { MB07: [0], MB08: [0], MB09: [0], MB15: [0], MB18: [0], MB21: [0], MB26: [0], MB35: [0] }
            }
        };

        const systemNameMappings = {
            "MB07": "5G700",
            "MB08": "L800",
            "MB09": "G/L900",
            "MB15": "5G1500",
            "MB18": "L1800",
            "MB21": "L2100",
            "MB26": "L2600",
            "MB35": "5G3700"
        };

        const allowedAntennaSystems = antennaSystemRules[operatorValue];
        const frequencyRule = frequencyRules[operatorValue];
        const numChannelsRule = numChannelsRules[operatorValue];
        const powerRule = powerRules[operatorValue]?.[vendorValue];
        const attenuationRule = attenuationRules[operatorValue]?.[vendorValue];

        if (!frequencyRule || !numChannelsRule || !powerRule || !attenuationRule) {
            return;
        }

        const nextAntennaSystems = [];

        for (const element of antennaSystemItems) {
            const frequency = frequencyRule[element];
            const txPower = powerRule[element];
            const attenuation = attenuationRule[element];
            const numChannels = numChannelsRule[element];

            if (frequency === undefined || !Array.isArray(txPower) || !Array.isArray(attenuation) || numChannels === undefined) {
                continue;
            }

            // Check if the antenna system is allowed for the current operator
            if (operatorValue !== "TELEKOM" && !allowedAntennaSystems.includes(element)) {
                continue;
            }

            const field = await antennaRest.getAntennaSystemField(antennaNameValue, frequency);
            if (!field) {
                continue;
            }

            const elecDowntilt = await antennaRest.getMaxElecDowntiltAntenna(antennaNameValue, frequency);
            nextAntennaSystems.push({
                antennaSystem: operatorValue === "TOWERS_AT" ? systemNameMappings[element] : element,
                frequency: frequency,
                elecDowntilt: elecDowntilt,
                antennaFrequency: field.frequency,
                antennaPort: field.port,
                txPower: txPower,
                attenuation: attenuation,
                numChannels: numChannels,
                selected: true
            });
        }

        if (nextAntennaSystems.length === 0) {
            return;
        }

        setSectors([
            { direction: 0, height: 30, mechDowntilt: 0, selected: true },
            { direction: 120, height: 30, mechDowntilt: 0, selected: true },
            { direction: 240, height: 30, mechDowntilt: 0, selected: true },
            { direction: 0, height: 30, mechDowntilt: 0, selected: false }
        ]);
        setAntennaSystems(nextAntennaSystems);
    };

    useOnChange(async (name, value) => {
        await initData(value);
    }, [antennaName]);

    useOnChange(async () => {
        await initData(antennaName.value);
    }, [vendor]);

    useOnChange(() => {
        clearDependentData();
        antennaName.set(null);
    }, [antennaManufacturer]);

    useOnChange(() => {
        if (operator.value && vendorMapping[operator.value]) {
            setVendorOptions(vendorMapping[operator.value]);
        } else {
            setVendorOptions({});
        }
        clearDependentData();
        vendor.set(null);
    }, [operator]);

    const updateSectorDirections = firstDirection => {
        setSectors(prevSectors => prevSectors.map((sector, index) => {
            if (index === 1) {
                return { ...sector, direction: (firstDirection + 120) % 360 };
            } else if (index === 2) {
                return { ...sector, direction: (firstDirection + 240) % 360 };
            }
            return sector;
        }));
    };

    const updateSectorHeights = firstHeight => {
        setSectors(prevSectors => prevSectors.map((sector, index) => {
            if (index !== 0) {
                return { ...sector, height: firstHeight };
            }
            return sector;
        }));
    };

    const onSave = async () => {
        setSaving(true);
        const allValid = await validationContext.allValid();
        validationContext.setErrorsVisible(true);
        let persisted = undefined;
        if (allValid) {
            //persisted entry contains generated ids
            entry.siteNum = siteNum.value;
            entry.name = entryName.value;
            try {
                if (entry.id === null) {
                    persisted = await entryRest.insert(entry);
                } else {
                    persisted = await entryRest.update(entry);
                }
                setLocation("/entry/" + persisted.id + "/radios");
            } catch (error) {
                console.error(error);
                setSaving(false);
            }
        } else {
            commonUtils.dispatchEvent("exception", { i18nLabel: "validateEntry" });
        }
        setSaving(false);
        return persisted;
    };

    const onAdd = () => {
        setEntry(prevEntry => {
            prevEntry.name = entryName.value;
            prevEntry.siteNum = siteNum.value;

            const updatedSectors = [...prevEntry.sectors];

            const newSectors = new Map();
            sectors.forEach(sector => {
                if (sector.selected) {
                    newSectors.set(sector, {
                        operator: operator.value,
                        antennaManufacturer: antennaManufacturer.value,
                        antennaName: antennaName.value,
                        height: sector.height,
                        direction: sector.direction,
                        mechDowntilt: sector.mechDowntilt,
                        positionX: 0,
                        positionY: 0,
                        radios: [],
                    });
                }
            });

            // system-major column order, as the flat list had it
            let column = updatedSectors.flatMap(sector => sector.radios).length;
            antennaSystems.forEach(antennaSystem => {
                if (antennaSystem.selected) {
                    newSectors.forEach(newSector => {
                        newSector.radios.push({
                            column: column++,
                            systemName: antennaSystem.antennaSystem,
                            antennaPort: antennaSystem.antennaPort,
                            antennaFrequency: antennaSystem.antennaFrequency,
                            frequency: antennaSystem.frequency,
                            txPower: antennaSystem.txPower[0],
                            antennaElecDowntilt: antennaSystem.elecDowntilt,
                            attenuation: antennaSystem.attenuation[0],
                            numChannels: antennaSystem.numChannels,
                            enabled: true,
                        });
                    });
                }
            });
            newSectors.forEach(newSector => updatedSectors.push(newSector));
            return { ...prevEntry, sectors: updatedSectors };
        });
        const newSelectedItems = [...selectedItems, antennaName.value];
        setSelectedItems(newSelectedItems);
    };

    return (
        <>
            <ContentHeader>
                <Button
                    svgIcon={mdiContentSave}
                    loading={saving}
                    onClick={() => onSave()}
                    value={intl.get("common.save")} />
                <Spacer variant="vertical" />
            </ContentHeader>
            <ContentBody>
                <ContentStyle>
                    <ColumnStyle>
                        <Text className="title">{intl.get("entry.siteNum")}</Text>
                    </ColumnStyle>
                    <ColumnStyle>
                        <InputText {...siteNum.bind}
                            title={false}
                            maxLength="15" />
                    </ColumnStyle>
                    <ColumnStyle>
                        <Text className="title">{intl.get("entry.name")}</Text>
                    </ColumnStyle>
                    <ColumnStyle>
                        <InputText {...entryName.bind}
                            title={false}
                            maxLength="90" />
                    </ColumnStyle>
                </ContentStyle>
                <ContentStyle>
                    <ColumnStyle>
                        <Spacer variant="vertical" />
                        <Text>{intl.get("entry.radio.operatorName")}</Text>
                        <Text>{intl.get("antennaSystem.vendor")}</Text>
                        <Text>{intl.get("entry.radio.antennaManufacturer")}</Text>
                        <Text>{intl.get("entry.radio.antennaName")}</Text>
                    </ColumnStyle>
                    <ColumnStyle>
                        <Spacer variant="vertical" />
                        <SelectSingle {...operator.bind}
                            title={false}
                            toLabel={value => (value ? intl.get("enum.operator." + value) : null)}>
                            <option value={null}></option>
                            <option value="TELEFONICA">{intl.get("enum.operator.TELEFONICA")}</option>
                            <option value="TELEKOM">{intl.get("enum.operator.TELEKOM")}</option>
                            <option value="VODAFONE">{intl.get("enum.operator.VODAFONE")}</option>
                            <option value="_1UND1">{intl.get("enum.operator._1UND1")}</option>
                            <option value="TOWERS_AT">{intl.get("enum.operator.TOWERS_AT")}</option>
                        </SelectSingle>
                        <SelectSingle {...vendor.bind}
                            title={false}
                            toLabel={value => {
                                if (!value) {
                                    return null;
                                }
                                const vendorKey = value.replace("VENDOR_", "");
                                return vendorOptions[vendorKey] || null;
                            }}>
                            <option value={null}></option>
                            {Object.entries(vendorOptions).map(([key, value]) => (
                                <option key={key} value={"VENDOR_" + key}>{value}</option>
                            ))}
                        </SelectSingle>
                        <SelectSingle {...antennaManufacturer.bind} title={false}>
                            {(async () => {
                                const manufacturers = await antennaRest.getManufacturers();
                                return manufacturers.map(manufacturer =>
                                    <option key={manufacturer} value={manufacturer}>{manufacturer}</option>
                                );
                            })}
                        </SelectSingle>
                        <Tooltip message={antennaName.value} enabled={!commonUtils.isBlank(antennaName.value)}>
                            <SelectSingle {...antennaName.bind} title={false} editable={true}>
                                {(async () => {
                                    const row = antenna => {
                                        const originType = ["P", "S", "T"].includes(antenna.originType) ? "N" : "F";
                                        return (
                                            <option key={antenna.id} value={antenna.name}>
                                                <AntennaNameRowStyle>
                                                    <div>{antenna.name}</div>
                                                    <div>{originType}</div>
                                                </AntennaNameRowStyle>
                                            </option>
                                        );
                                    };
                                    if (!antennaManufacturer.value) {
                                        return [];
                                    }
                                    const antennas = await antennaRest.getAntennasByManufacturer(antennaManufacturer.value);
                                    // Update sectors state
                                    return antennas.map(antenna => row(antenna));
                                })}
                            </SelectSingle>
                        </Tooltip>
                    </ColumnStyle>
                </ContentStyle>
                <ContentStyle>
                    <ColumnStyle>
                        <Spacer variant="vertical" />
                        <Text>{intl.get("navigation.antennas")}</Text>
                        <Text>{intl.get("entry.radio.height")}</Text>
                        <Text>{intl.get("entry.radio.direction")}</Text>
                        <Text>{intl.get("entry.radio.mechDowntilt")}</Text>
                    </ColumnStyle>
                    {sectors.map((sector, index) =>
                        <div key={index}>
                            <ColumnStyle>
                                <Spacer variant="vertical" />
                                <SectorForm index={index} key={index} sector={sector} updateDirection={updateSectorDirections} updateHeight={updateSectorHeights} />
                            </ColumnStyle>
                        </div>
                    )}
                </ContentStyle>
                <ContentStyle>
                    <ColumnStyle>
                        <Spacer variant="vertical" />
                        <Text>{intl.get("antennaSystem.system")}</Text>
                        <Text>{intl.get("entry.radio.systemName")}</Text>
                        <Text>{intl.get("entry.radio.frequency")}</Text>
                        <Text>{intl.get("entry.radio.txPower")}</Text>
                        <Text>{intl.get("entry.radio.antennaElecDowntilt")}</Text>
                        <Text>{intl.get("entry.radio.attenuation")}</Text>
                        <Text>{intl.get("entry.radio.numChannels")}</Text>
                    </ColumnStyle>
                    {antennaSystems.map((antennaSystem, index) =>
                        <div key={index}>
                            <ColumnStyle>
                                <Spacer variant="vertical" />
                                <AntennaSystemForm index={index} key={index} antennaSystem={antennaSystem} />
                            </ColumnStyle>
                        </div>
                    )}
                </ContentStyle>
                <ContentStyle>
                    <ColumnStyle>
                        <Spacer variant="vertical" />
                        <Text>{intl.get("antennaSystem.selection")}</Text>
                    </ColumnStyle>
                    {selectedItems.map((selectedItem, index) =>
                        <div key={index}>
                            <ColumnStyle>
                                <Spacer variant="vertical" />
                                <Text>{selectedItem}</Text>
                            </ColumnStyle>
                        </div>
                    )}
                </ContentStyle>
                <ContentStyle>
                    <ColumnStyle>
                        <Spacer variant="vertical" />
                        <Button
                            svgIcon={mdiPlusBox}
                            onClick={() => onAdd()}
                            value={intl.get("antennaSystem.add")} />
                        <Spacer variant="vertical" /></ColumnStyle>
                </ContentStyle>
            </ContentBody>
        </>
    );
};